import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { 
  Plus, 
  Upload,
  MagnifyingGlass, 
  Funnel,
  Folder,
  MapPin,
  User
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { ETAPES_CHANTIER, ETAPE_CLASSE, STATUT_CHANTIER, etapeChantier, libelleEtape } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Chantiers() {
  const { canEdit } = useAuth();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [chantierBadges, setChantierBadges] = useState({});

  useEffect(() => {
    fetchChantiers();
  }, []);

  useEffect(() => {
    // M2 Assistant — Badge "+X nouveaux éléments" sur chaque chantier
    axios.get(`${API_URL}/api/dashboard/since-last-crc-summary`, { withCredentials: true })
      .then((r) => {
        const map = {};
        (r.data?.summaries || []).forEach((s) => { map[s.chantier_id] = s.total; });
        setChantierBadges(map);
      })
      .catch(() => {});
  }, []);

  const fetchChantiers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers`, { withCredentials: true });
      setChantiers(data);
    } catch (err) {
      console.error('Error fetching chantiers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredChantiers = chantiers.filter(c => {
    const q = (search || '').toLowerCase();
    const name = (c.name || '').toLowerCase();
    const addr = (c.address || c.adresse || '').toLowerCase();
    const owner = (c.owner_name || '').toLowerCase();
    // B1 — filtre sur l'étape unique (le statut est déduit de l'étape)
    if (statusFilter !== 'all' && etapeChantier(c) !== statusFilter) return false;
    return name.includes(q) || addr.includes(q) || owner.includes(q);
  });

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="chantiers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Chantiers
            </h1>
            <p className="text-[#6B6B6B] mt-1">
              {chantiers.length} chantier{chantiers.length > 1 ? 's' : ''} au total
            </p>
          </div>
          {canEdit && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/chantiers/new">
                <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2 w-full sm:w-auto" data-testid="new-chantier-button">
                  <Plus size={18} weight="bold" />
                  Nouveau chantier
                </Button>
              </Link>
              <Link to="/chantiers/import">
                <Button
                  variant="outline"
                  className="bg-white hover:bg-[#F5F5F3] text-[#2C2C2C] border-[#2C2C2C] rounded-none gap-2 w-full sm:w-auto"
                  aria-label="Importer un chantier existant"
                  title="Importer un chantier déjà commencé (DPGF, planning, entreprises)"
                  data-testid="import-chantier-button"
                >
                  <Upload size={18} weight="bold" />
                  Importer chantier existant
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <Input
              placeholder="Rechercher un chantier..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-none border-[#E0E0DB]"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-[#6B6B6B]" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-11 rounded-none border-[#E0E0DB]" data-testid="status-filter">
                <SelectValue placeholder="Étape" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les étapes</SelectItem>
                {ETAPES_CHANTIER.map((e) => <SelectItem key={e.cle} value={e.cle}>{e.libelle}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Chantiers Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        ) : filteredChantiers.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E0E0DB]">
            <Folder size={64} className="mx-auto text-[#E0E0DB] mb-4" />
            <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucun chantier trouvé</h3>
            <p className="text-[#6B6B6B] mb-6">
              {search || statusFilter !== 'all' 
                ? 'Essayez de modifier vos filtres de recherche'
                : 'Commencez par créer votre premier chantier'}
            </p>
            {canEdit && !search && statusFilter === 'all' && (
              <div className="flex flex-col items-center gap-3">
                <Link to="/chantiers/new">
                  <Button className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none">
                    Créer un chantier
                  </Button>
                </Link>
                <Link
                  to="/chantiers/import"
                  className="text-sm text-[#6B6B6B] hover:text-[#2C2C2C] underline underline-offset-2"
                  data-testid="import-chantier-empty-link"
                >
                  Ou importer un chantier déjà commencé
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChantiers.map((chantier) => (
              <Link
                key={chantier.id}
                to={`/chantiers/${chantier.id}`}
                className="bg-white border border-[#E0E0DB] hover:border-[#2C2C2C] transition-colors"
                data-testid={`chantier-card-${chantier.id}`}
              >
                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-semibold text-lg text-[#2C2C2C] break-words" title={chantier.name}>{chantier.name}</h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {chantierBadges[chantier.id] > 0 && (
                        <span
                          className="text-[10px] px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded font-semibold"
                          data-testid={`chantier-badge-new-${chantier.id}`}
                        >
                          +{chantierBadges[chantier.id]}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 uppercase tracking-wider font-bold ${ETAPE_CLASSE[etapeChantier(chantier)] || ''}`} data-testid={`chantier-etape-${chantier.id}`}>
                        {chantier.etape_libelle || libelleEtape(etapeChantier(chantier))}
                      </span>
                      {['prospection', 'en_pause'].includes(chantier.status) && (
                        <span className="text-[10px] text-[#6B6B6B] border border-[#E0E0DB] px-1.5 py-0.5">{STATUT_CHANTIER[chantier.status]}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-[#6B6B6B] mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} />
                      <span className="truncate">{chantier.address || chantier.adresse || <span className="italic text-[#9A9A9A]">Adresse non renseignée</span>}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span className="truncate">{chantier.owner_name || <span className="italic text-[#9A9A9A]">Maître d'ouvrage non renseigné</span>}</span>
                    </div>
                  </div>
                  
                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[#6B6B6B]">Progression</span>
                      <span className="font-medium text-[#2C2C2C]">{chantier.progress || 0}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${chantier.progress || 0}%` }} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
